import React, { useEffect, useState } from 'react';
import { DeskTable, ElementTable, ElementCell, ByView } from '../../components/nucleus/Shelf';
import NucleusPage, { Verb, FindField } from '../../components/nucleus/NucleusPage';
import { SHELVES } from '../../components/nucleus/shelves';
import { useFacets, Faceted } from '../../components/nucleus/FacetRail';
import { Cell, Row } from '../../components/nucleus/Collection';
import { TitledSet } from '../../components/nucleus/SetHead';
import Inspector, { Band, Note, useOpen } from '../../components/nucleus/Inspector';
import { AppFace, elementOf } from '../../components/nucleus/cells';
import { AgentFace } from '../../components/nucleus/faces';
import { ConnectedCell, AppTable, StoreCell, StoreTable, Line } from './AppBoard';
import { useToast } from '../../components/common';
import { usePlace } from '../../nucleus/place';
import { useSpaceAccess } from '../../nucleus/access';
import AppInspector from './AppInspector';
import BillingView from './BillingView';
import {
    useApps, useUsage, shelfOf, statusOf, grantHref, appsApi,
    loadAppGrants, loadDeclaredAgents, agentNames, agentGrantHref, opsLine, ownedWord,
} from './appsData';
import { Plus, Code2 } from 'lucide-react';
import api from '../../services/api';
import { plainSummary } from '../activity/activityData';
import StatusPill from '../../components/nucleus/StatusPill';
import './apps.css';
import { ago, dateOf } from '../../nucleus/format';
import { statusWord } from '../../components/nucleus/status';

// building your own: the developer portal, from the store
const DEVELOPER = 'https://developer.ofself.com';

// APPS — the apps you let in. Cards show each app's few facts, as squares;
// rows show everything there is to compare. Connected: what each did in the last
// 30 days (and the ones whose contracts expired). The Store: every
// app by shelf, how many people use it, its price, yours marked. Then the Agents
// your apps have here (allowed, or declared and not yet), and Billing.
export default function AppsSection() {
    const { page } = usePlace();
    if (page?.id === 'billing') return <BillingView />;
    if (page?.id === 'agents') return <AgentsPage />;
    return <AppsPage store={page?.id === 'store'} />;
}

// A member sees an admin's acts on apps greyed, each with the one same why.
export function useAppsGate() {
    const access = useSpaceAccess();
    const hint = access.onlyAdmin('change its apps');
    return { isAdmin: access.isAdmin, hint, off: access.isAdmin ? {} : { disabled: true, hint } };
}

// what an app is found by: its name, its description, its element's symbol
const textOf = ({ app }) => [app.name, app.description, elementOf(app)?.symbol].filter(Boolean).join(' ');

// the filter rail's facets: how it stands here, what it costs, its shelf
const SHELF_WORD = Object.fromEntries(SHELVES.map((s) => [s.id, s.label]));
const FACETS = [
    { id: 'status', title: 'Status', of: (e) => (e.row ? statusOf(e.row) : null), order: ['active', 'paused', 'expired'], always: true, label: (k) => statusWord(k, 'app') },
    { id: 'price', title: 'Price', of: (e) => (e.app.pricing_display?.is_paid ? 'paid' : 'free'), order: ['free', 'paid'], always: true, label: (k) => (k === 'paid' ? 'Paid' : 'Free') },
    { id: 'shelf', title: 'Category', of: (e) => shelfOf(e.app), order: SHELVES.map((s) => s.id), label: (k) => SHELF_WORD[k] || k },
];

function AppsPage({ store }) {
    const { groupId, spaceName, inSpace, section, page, go } = usePlace();
    const gate = useAppsGate();
    const data = useApps(groupId);
    const usage = useUsage(groupId);
    const [openId, open, close] = useOpen();
    const toast = useToast();

    const entry = openId ? data.find(openId) : null;
    const reload = () => { data.reload(); usage.reload(); };

    // the same acts as the inspector's, on a right-click
    const quick = (e) => {
        const { app, row } = e;
        const status = row ? statusOf(row) : null;
        const acts = [{ id: 'about', label: `About ${app.name}`, run: () => open(app.id) }];
        if (status === 'active' && app.website_url) acts.push({ id: 'open', label: 'Open', primary: true, run: () => window.open(app.website_url, '_blank', 'noopener') });
        if (!row && app.client_id) acts.push({ id: 'connect', label: 'Connect', primary: true, ...gate.off, run: () => { window.location.href = grantHref(app, groupId); } });
        if (status === 'paused') acts.push({ id: 'resume', label: 'Resume', ...gate.off, run: async () => { await appsApi.resume(groupId, row.ep); toast.success(`${app.name} is running again.`); reload(); } });
        if (status === 'active' || status === 'paused') {
            acts.push({
                id: 'disconnect', label: 'Disconnect', danger: true, ...gate.off,
                confirm: { title: `Disconnect ${app.name}?`, body: `${app.name} loses its reach into ${spaceName}. ${groupId ? `${spaceName}’s` : 'Your'} data stays where it is.` },
                run: async () => { await appsApi.disconnect(groupId, row.ep); toast.success(`${app.name} is disconnected.`); reload(); },
            });
        }
        return acts;
    };

    const all = store ? data.everything : [...data.connected, ...data.lapsed];
    const f = useFacets(all, FACETS, { text: textOf, placeholder: store ? 'Find an app' : 'Filter apps' });
    const keep = new Set(f.shown);
    const shown = all.filter((e) => keep.has(e));
    const total = store ? data.everything.length : data.connected.length;
    const loadedEmpty = (text) => (data.loaded ? (f.filtering ? 'Nothing matches the filters.' : text) : undefined);
    const isOpen = (e) => String(e.app.id) === String(openId);

    let body;
    if (!store) {
        const live = shown.filter((e) => data.connected.includes(e));
        const ended = shown.filter((e) => data.lapsed.includes(e));
        const cells = (list) => list.map((e) => <ConnectedCell key={e.app.id} entry={e} usage={usage} current={isOpen(e)} onPress={() => open(e.app.id)} actions={quick(e)} />);
        body = (
            <ByView
                cards={(
                    <DeskTable top={0} between={40}>
                        <ElementTable title={`Connected${inSpace}`} count={data.loaded ? live.length : undefined} empty={loadedEmpty('Nothing connected yet. The store has every app.')}>{cells(live)}</ElementTable>
                        {ended.length > 0 && <ElementTable title="Contract expired" count={ended.length}>{cells(ended)}</ElementTable>}
                    </DeskTable>
                )}
                rows={(
                    <DeskTable top={0} between={40}>
                        <TitledSet title={`Connected${inSpace}`} count={data.loaded ? live.length : undefined} empty={loadedEmpty('Nothing connected yet. The store has every app.')}>
                            {live.length > 0 && <AppTable entries={live} usage={usage} openId={openId} onOpen={open} actionsOf={quick} />}
                        </TitledSet>
                        {ended.length > 0 && <TitledSet title="Contract expired" count={ended.length}><AppTable id="apps-expired" entries={ended} usage={usage} openId={openId} onOpen={open} actionsOf={quick} /></TitledSet>}
                    </DeskTable>
                )}
            />
        );
    } else {
        // one store; the categories are the filter's. What you could add comes
        // first; the apps already connected here follow, each saying so.
        // the same order as the table's: not yours first, the newest listed first
        const when = (e) => Date.parse(e.app.created_at || '') || 0;
        const tiles = [...shown].sort((a, b) => (ownedWord(a) ? 1 : 0) - (ownedWord(b) ? 1 : 0) || when(b) - when(a));
        const cells = (list) => list.map((e) => <StoreCell key={e.app.id} entry={e} current={isOpen(e)} onPress={() => open(e.app.id)} actions={quick(e)} />);
        body = (
            <ByView
                cards={(
                    <DeskTable top={0} between={40}>
                        <ElementTable title="All apps" count={data.loaded ? shown.length : undefined} empty={loadedEmpty('No apps in the store yet.')}>{cells(tiles)}</ElementTable>
                    </DeskTable>
                )}
                rows={(
                    <DeskTable top={0} between={40}>
                        <TitledSet title="All apps" count={data.loaded ? shown.length : undefined} empty={loadedEmpty('No apps in the store yet.')}>
                            {shown.length > 0 && <StoreTable entries={tiles} openId={openId} onOpen={open} actionsOf={quick} />}
                        </TitledSet>
                    </DeskTable>
                )}
            />
        );
    }

    return (
        <NucleusPage
            section={section.label}
            title={page.label}
            count={data.loaded ? total : undefined}
            verbs={store
                ? <Verb icon={<Code2 />} onClick={() => window.open(DEVELOPER, '_blank', 'noopener')}>Build an app</Verb>
                : <Verb primary icon={<Plus />} disabled={!gate.isAdmin} title={gate.hint} onClick={() => go('store')}>Add an app</Verb>}
            inspector={entry && <AppInspector key={entry.app.id} entry={entry} usage={entry.row ? usage.of(entry.app.id) : null} days={usage.days} onClose={close} onChanged={reload} />}
        >
            <Faceted id={store ? 'apps-store' : 'apps'} rail={f.rail}>
                {body}
            </Faceted>
        </NucleusPage>
    );
}

/** An agent's face: the agent glyph in its app's hue, the app's mark as a seal. */
function SubEntityFace({ name, parent, hue }) {
    return (
        <span className="apps-subentity" role="img" aria-label={`${name || 'Agent'}, agent of ${parent?.name || 'an app'}`}>
            <AgentFace size={50} hue={hue} />
            <span className="apps-subentity__seal"><AppFace app={parent} size={18} /></span>
        </span>
    );
}

// AGENTS — the agents (sub-entities) your apps have here: each one allowed (a
// live contract, paused or not — "allowed", not "running": it says nothing of
// whether it is at work), and the agents your apps declare that are not allowed
// here yet, each with the way to allow it.
function AgentsPage() {
    const { groupId, spaceName, inSpace, section, page, to, navigate } = usePlace();
    const gate = useAppsGate();
    const apps = useApps(groupId);
    const usage = useUsage(groupId);
    const toast = useToast();
    const [eps, setEps] = useState(null);
    const [declared, setDeclared] = useState([]);
    const [find, setFind] = useState('');
    const [openId, open, close] = useOpen();
    const [tick, setTick] = useState(0);
    useEffect(() => {
        let live = true;
        Promise.all([loadAppGrants(groupId), loadDeclaredAgents(groupId)]).then(([g, d]) => {
            if (!live) return;
            setEps(g);
            setDeclared(d);
        });
        return () => { live = false; };
    }, [groupId, tick]);
    const reload = () => { setTick((t) => t + 1); usage.reload(); };
    const grants = eps ? eps.filter((ep) => ep.sub_entity_key) : null;
    // each app's own grant here, which its agents' are measured against
    const appGrantOf = (appId) => (eps || []).find((ep) => !ep.sub_entity_key && String(ep.entity_id) === String(appId));

    const appOf = (id) => apps.catalog.find((a) => String(a.id) === String(id)) || { id, name: 'App' };
    const byClient = (clientId) => apps.catalog.find((a) => a.client_id === clientId) || null;
    const nameOf = agentNames(declared);
    const rosterOf = (ep) => declared.find((a) => String(a.parent_app_id || a.app_id) === String(ep.entity_id) && a.sub_entity_key === ep.sub_entity_key);
    const q = find.trim().toLowerCase();
    const hit = (name) => !q || String(name).toLowerCase().includes(q);
    const granted = new Set((grants || []).map((ep) => `${ep.entity_id}:${ep.sub_entity_key}`));
    const offered = declared.filter((a) => !granted.has(`${a.parent_app_id || a.app_id}:${a.sub_entity_key}`));

    const actionsOf = (ep) => {
        const name = nameOf(ep);
        const off = ep.is_active === false;
        return [
            off
                ? { id: 'resume', label: 'Resume', primary: true, ...gate.off, run: async () => { await appsApi.resume(groupId, ep); toast.success(`${name} is running again.`); reload(); } }
                : { id: 'pause', label: 'Pause', ...gate.off, run: async () => { await appsApi.pause(groupId, ep); toast.success(`${name} is paused.`); reload(); } },
            {
                id: 'disconnect', label: 'Disconnect', danger: true, ...gate.off,
                confirm: { title: `Disconnect ${name}?`, body: `${name} loses its reach into ${spaceName}. Its app keeps its own.` },
                run: async () => { await appsApi.disconnect(groupId, ep); toast.success(`${name} is disconnected.`); close(); reload(); },
            },
        ];
    };
    // ALLOW: a declared agent's way in is the consent page, for that agent, under
    // its app's grant here — so its app must be connected here first
    const allowOf = (a) => {
        const parent = appOf(a.parent_app_id || a.app_id);
        const held = !!appGrantOf(parent.id);
        const why = !gate.isAdmin ? gate.hint : !held ? `Connect ${parent.name} here first` : !parent.client_id ? 'Its app cannot be reached' : undefined;
        return {
            id: 'allow', label: 'Allow', primary: true, disabled: !!why, hint: why,
            run: () => { window.location.href = agentGrantHref(parent, a.sub_entity_key, groupId); },
        };
    };

    const openEp = (grants || []).find((ep) => String(ep.id) === String(openId));
    // what the open agent did last (its five newest entries), as an app's panel shows
    const [recent, setRecent] = useState(undefined);
    const openVia = openEp ? `${openEp.entity_id}:${openEp.sub_entity_key}` : null;
    useEffect(() => {
        setRecent(undefined);
        if (!openVia) return undefined;
        let live = true;
        api.getActivityFeed({ via: openVia, per_page: '5', include_facets: 'false' }, groupId)
            .then((r) => { if (live) setRecent(r?.entries || []); }).catch(() => { if (live) setRecent([]); });
        return () => { live = false; };
    }, [openVia, groupId]);
    const openDeclared = !openEp && offered.find((a) => String(a.id) === String(openId));
    let inspector = null;
    if (openEp) {
        const parent = appOf(openEp.entity_id);
        const about = rosterOf(openEp)?.description;
        const u = usage.agent(openEp.entity_id, openEp.sub_entity_key);
        const caller = openEp.calling_app_client_id && openEp.calling_app_client_id !== parent.client_id
            ? (byClient(openEp.calling_app_client_id) || { name: 'another app' }) : null;
        // AN AGENT READS LIKE AN APP, in the app panel's order: what it did, its
        // contract, then where it comes from: the app that made it, and the app
        // named as its caller when that is another app.
        const status = openEp.is_active === false ? 'paused' : 'active';
        inspector = (
            <Inspector
                key={openEp.id}
                // ITS OWN FACE, as its cell wears it — the app's mark was every
                // agent of one app looking like the app itself
                face={<SubEntityFace name={nameOf(openEp)} parent={parent} hue={elementOf(parent)?.hue} />}
                tag={['Agent', statusWord(status, 'app')].join(' · ')}
                title={nameOf(openEp)}
                sub={about || `An agent of ${parent.name}`}
                actions={[
                    { id: 'app', label: `Open ${parent.name}`, primary: true, run: () => navigate(to('apps', null, `open=${parent.id}`)) },
                    parent.client_id && { id: 'edit', label: 'Edit access', ...gate.off, run: () => { window.location.href = `${agentGrantHref(parent, openEp.sub_entity_key, groupId)}&edit_auth_id=${encodeURIComponent(openEp.id)}`; } },
                    ...actionsOf(openEp),
                ].filter(Boolean)}
                onClose={close}
            >
                <Band title={`Last ${usage.days} days`}>
                    <p className="apps-ops">{opsLine(u)}</p>
                    <div className="apps-inspector-line"><Line days={u.by_day} legend tall /></div>
                </Band>
                <Band title="Recent" aside={<button type="button" className="nx-link" onClick={() => navigate(to('activity', 'log', `via=${encodeURIComponent(openVia)}`))}>All activity</button>}>
                    {recent === undefined && <Note>Reading…</Note>}
                    {recent && recent.length === 0 && <Note>Nothing yet.</Note>}
                    {(recent || []).map((e) => (
                        <Row key={e.id} name={plainSummary(e)} end={ago(e.timestamp)} onPress={() => navigate(to('activity', 'log', `open=${e.id}`))} />
                    ))}
                </Band>
                <Band title="Its contract" aside={<StatusPill status={status} of="contract" />}>
                    <Row
                        name={openEp.privacy_realm?.name || 'No realm'}
                        sub={[
                            `Since ${dateOf(openEp.created_at)}`,
                            openEp.expires_at ? `Ends ${dateOf(openEp.expires_at)}` : 'Never ends',
                        ].join(' · ')}
                        end="Open"
                        onPress={() => navigate(to('realms', 'profiles', `open=app:${openEp.entity_id}:${openEp.sub_entity_key}`))}
                    />
                </Band>
                <Band title="About">
                    <Row name={`Originating app: ${parent.name}`} sub="The app that made it" end="Open" onPress={() => navigate(to('apps', null, `open=${parent.id}`))} />
                    {/* only when another app was named as its caller: per-request callers
                        are not recorded, so "none" would not be the truth either */}
                    {caller && (
                        <Row
                            name={`Calling app: ${caller.name}`}
                            sub="Another app uses it here"
                            end={caller.id ? 'Open' : undefined}
                            onPress={caller.id ? () => navigate(to('apps', null, `open=${caller.id}`)) : undefined}
                        />
                    )}
                    <Note>{u.last_active ? `Last active ${ago(u.last_active)}.` : 'Not active here yet.'}</Note>
                </Band>
            </Inspector>
        );
    } else if (openDeclared) {
        const parent = appOf(openDeclared.parent_app_id || openDeclared.app_id);
        inspector = (
            <Inspector
                key={openDeclared.id}
                face={<SubEntityFace name={openDeclared.display_name || 'Agent'} parent={parent} hue={elementOf(parent)?.hue} />}
                tag={openDeclared.parent_app_name ? `Agent of ${openDeclared.parent_app_name}` : 'Agent'}
                title={openDeclared.display_name || 'Agent'}
                sub="Declared by its app, not allowed here yet."
                actions={[allowOf(openDeclared)]}
                onClose={close}
            >
                {openDeclared.description && <Band title="What it is for"><Note>{openDeclared.description}</Note></Band>}
            </Inspector>
        );
    }

    // an agent wears the agent's face in its app's hue, with its app's mark as a
    // small seal (never initials: design.oS draws no letters as a face)
    const agentCell = (key, name, parentId, note, current, onPress, actions) => {
        const parent = appOf(parentId);
        return (
            <Cell key={key} actions={actions}>
                <ElementCell
                    hue={elementOf(parent)?.hue || '#8a8378'} mark={<SubEntityFace name={name} parent={parent} hue={elementOf(parent)?.hue} />}
                    name={name} note={note} dim={!hit(name)} current={current} onPress={onPress} title={`Agent of ${parent.name}`}
                />
            </Cell>
        );
    };

    return (
        <NucleusPage
            section={section.label}
            title={page.label}
            count={grants ? grants.length : undefined}
            verbs={<FindField value={find} onChange={setFind} placeholder="Find an agent" />}
            inspector={inspector}
        >
            <DeskTable top={0} between={40}>
                <ElementTable title={`Allowed${inSpace}`} count={grants?.length} empty={grants ? 'No agent is allowed here.' : undefined}>
                    {(grants || []).map((ep) => agentCell(
                        ep.id, nameOf(ep), ep.entity_id,
                        `${appOf(ep.entity_id).name}${ep.is_active === false ? ' · paused' : ''}`,
                        String(ep.id) === String(openId), () => open(ep.id), actionsOf(ep),
                    ))}
                </ElementTable>
                {offered.length > 0 && (
                    <ElementTable title="Declared, not yet allowed" count={offered.length}>
                        {offered.map((a) => agentCell(
                            a.id, a.display_name || 'Agent', a.parent_app_id || a.app_id, `${appOf(a.parent_app_id || a.app_id).name} · not allowed`,
                            String(a.id) === String(openId), () => open(a.id), [allowOf(a)],
                        ))}
                    </ElementTable>
                )}
            </DeskTable>
        </NucleusPage>
    );
}

